import { api } from "@/lib/axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
    ItemCodeParams,
    ItemCodeResponse,
    ItemCodeSearchParams,
    ItemCode,
} from "@/interface/standard/st_item_code";
import { toast } from "sonner";

export const useItemCode = (params: ItemCodeParams) => {
  const { page = 0, size = 20, search, sort, exactMatch } = params;
  return useQuery<ItemCodeResponse>({
    queryKey: ["itemCode", page, size, search, sort, exactMatch],
    queryFn: async () => {
        const response = await api.get("/item-code", {
            params: {
                page,
                size,
                stItemName: search,
                sort,
                exactMatch
            },
        });
        return response.data;
    },
    staleTime: 1000 * 60, // 1분간 캐시 유지
    refetchOnMount: true,
  });
};

export const useItemCodeSearch = (params: ItemCodeSearchParams) => {
  const { search, searchType, page, size, enabled } = params;
  return useQuery<any>({
    queryKey: ["itemCodeSearch", search, searchType, page, size],
    queryFn: async () => {
      const response = await api.get("/item-code/search", { params: { search, searchType, page, size } });
      return response.data;
    },
    enabled: Boolean(enabled) && typeof search === "string" && search.length > 0,
  });
};

// 품목코드 추가
export const useCreateItemCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<ItemCode>) => {
      const response = await api.post("/item-code", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itemCode"] });
      toast.success("품목코드가 추가되었습니다.");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "품목코드 추가에 실패했습니다.");
    },
  });
};

// 품목코드 수정
export const useUpdateItemCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ stItemCode, data }: { stItemCode: string; data: Partial<ItemCode> }) => {
      const response = await api.put(`/item-code/${stItemCode}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itemCode"] });
      toast.success("품목코드가 수정되었습니다.");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "품목코드 수정에 실패했습니다.");
    },
  });
};

// 품목코드 삭제
export const useDeleteItemCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (stItemCode: string) => {
      const response = await api.delete(`/item-code/${stItemCode}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itemCode"] });
      toast.success("품목코드가 삭제되었습니다.");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "품목코드 삭제에 실패했습니다.");
    },
  });
};

// 다음 품목코드 조회 (년도 + 순번 자동 생성)
export const useNextItemCode = (enabled: boolean = true) => {
  return useQuery<string>({
    queryKey: ["nextItemCode"],
    queryFn: async () => {
      const response = await api.get("/item-code/next-code");
      return response.data;
    },
    enabled: enabled,
    gcTime: 0, // 캐시 사용 안 함 (항상 최신 코드 가져오기)
    refetchOnMount: true,
  });
};

// CAS 번호로 유해위험분류 조회 (mutation - 명시적 호출만)
export const useSearchCasNo = () => {
  return useMutation({
    mutationFn: async (data: Partial<ItemCode>) => {
      const response = await api.post(`/item-code/cas-no`, data );
      return response.data;
    },
  });
};

// 즐겨찾기 토글
export const useToggleFavorite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (stItemCode: string) => {
      const response = await api.put(`/item-code/${stItemCode}/favorite`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itemCode"] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "즐겨찾기 변경에 실패했습니다.");
    },
  });
};